import React, { useEffect, useRef, useState } from "react";
import {
  chordsForProgression,
  audioSupported,
  midiSupported,
  midiToKey,
  midiRangeShift,
} from "../../lib/keyboardPlan";
import {
  TEMPO_MIN,
  TEMPO_MAX,
  beatMsToBpm,
  bpmToBeatMs,
  UNLOCK_CODE,
} from "../../lib/numberSenseData";
import { Icon, Keys, Piano, PlayButton, AudioSettingsContext } from "../numberSenseUi";
import { useMidiMonitor } from "./useMidi";

/* ============================================================
   Number Sense — settings (audio).
   Mute, ear-training tempo, and an optional count-in click before
   loops. Changes are lifted to the page via onChange and persisted
   alongside progress; the page also pushes mute into the engine.

   Props:
     settings : { muted, tempo, countIn }
     onChange : (patch) => void
     onBack   : () => void
   ============================================================ */
const CAN_PLAY = audioSupported();
const MIDI_SUPPORTED = midiSupported();
const EMPTY_SET = new Set();
const noop = () => {};
// A short, recognisable loop to preview the current audio settings.
const SAMPLE = chordsForProgression("C", ["I", "IV", "V", "I"]);

// A "C4"-style id for a MIDI note number, for highlighting the test piano.
const noteId = (midi) => {
  const k = midiToKey(midi);
  return `${k.name}${k.octave}`;
};

/* The keyboard-range calibration. Small (25-key) or octave-shifted keyboards
   send notes outside the screen piano's two-octave window, so most keys sound
   but never light up. "Map my keyboard" asks for the lowest key, then the
   highest — the raw (un-mapped) notes are stored with settings, and every
   incoming note is then shifted by whole octaves so the keyboard's range
   fits the screen. Capture reads `last.raw` off the monitor so an existing
   calibration can't distort a re-calibration. */
const RangeCalibration = ({ last, settings, onChange }) => {
  const [step, setStep] = useState(null); // null | "low" | "high"
  const [low, setLow] = useState(null); // captured lowest, while step === "high"
  const lastRef = useRef(last);

  useEffect(() => {
    if (last === lastRef.current) return; // no new press
    lastRef.current = last;
    if (!last || step == null) return;
    if (step === "low") {
      setLow(last.raw);
      setStep("high");
    } else if (last.raw !== low) {
      // pressed out of order still works — the pair is stored sorted
      onChange({
        midiLow: Math.min(low, last.raw),
        midiHigh: Math.max(low, last.raw),
      });
      setStep(null);
      setLow(null);
    }
  }, [last, step, low, onChange]);

  const calibrated = settings.midiLow != null && settings.midiHigh != null;
  const shift = calibrated
    ? midiRangeShift(settings.midiLow, settings.midiHigh)
    : 0;
  const octaves = Math.abs(shift) / 12;

  return (
    <div className="ns-midi-cal">
      {step === "low" ? (
        <p className="ns-set-sub">
          press the <b>lowest</b> key on your keyboard…
        </p>
      ) : step === "high" ? (
        <p className="ns-set-sub">
          got {noteId(low)} — now press the <b>highest</b> key…
        </p>
      ) : calibrated ? (
        <p className="ns-set-sub">
          your keyboard: {noteId(settings.midiLow)}–{noteId(settings.midiHigh)}
          {shift === 0
            ? " — it already fits the screen keys"
            : ` — shifted ${shift > 0 ? "up" : "down"} ${octaves} octave${
                octaves === 1 ? "" : "s"
              } to fit the screen keys`}
        </p>
      ) : (
        <p className="ns-set-sub">
          keys not lighting up? map your keyboard's range and it'll be shifted
          to fit the screen keys.
        </p>
      )}
      <div className="ns-midi-cal-btns">
        {step ? (
          <button
            type="button"
            className="ns-hint-btn"
            onClick={() => {
              setStep(null);
              setLow(null);
            }}
          >
            cancel
          </button>
        ) : (
          <>
            <button
              type="button"
              className="ns-hint-btn"
              onClick={() => setStep("low")}
            >
              {calibrated ? "re-map my keyboard" : "map my keyboard"}
            </button>
            {calibrated && (
              <button
                type="button"
                className="ns-hint-btn"
                onClick={() => onChange({ midiLow: null, midiHigh: null })}
              >
                clear
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* The MIDI setup + tester. Lists the connected keyboard(s) and echoes every
   incoming note so a player can verify each key registers — the on-screen
   keys light as they're held, and every distinct note pressed is logged
   below (so a quick tap still leaves a trace, even out of the piano's range). */
const MidiSetup = ({ settings, onChange }) => {
  const { status, deviceNames, held, seen, last, reset } = useMidiMonitor();
  const heldIds = new Set([...held].map(noteId));
  const seenSorted = [...seen].sort((a, b) => a - b);

  return (
    <div className="ns-midi-setup">
      <div className="ns-set-h">MIDI keyboard</div>

      {status === "idle" && (
        <p className="ns-set-sub">checking for a MIDI keyboard…</p>
      )}
      {status === "denied" && (
        <p className="ns-set-sub">
          MIDI access is blocked. Allow MIDI for this site in your browser's
          site settings, then reload the page.
        </p>
      )}
      {status === "no-devices" && (
        <p className="ns-set-sub">
          No keyboard detected yet. Connect one over USB — it should appear
          here on its own, no reload needed.
        </p>
      )}
      {status === "connected" && (
        <p className="ns-set-sub">
          connected: {deviceNames.join(", ")}. Press each key to check it
          registers.
        </p>
      )}

      {(status === "connected" || status === "no-devices") && (
        <div className="ns-midi-tester">
          <div className="ns-midi-last">
            {last ? (
              <>
                <b className="mono">
                  {last.name}
                  {last.octave}
                </b>
                <span className="ns-set-sub">MIDI {last.midi}</span>
              </>
            ) : (
              <span className="ns-set-sub">press any key to test it…</span>
            )}
          </div>

          <Piano pressed={EMPTY_SET} exactKeys={heldIds} onPress={noop} disabled />

          {status === "connected" && (
            <RangeCalibration last={last} settings={settings} onChange={onChange} />
          )}

          <div className="ns-midi-seen">
            <span className="ns-set-sub">keys seen this session: {seen.size}</span>
            {seenSorted.length > 0 && (
              <div className="ns-midi-seen-chips">
                {seenSorted.map((m) => (
                  <span key={m} className="ns-midi-chip mono">
                    {noteId(m)}
                  </span>
                ))}
              </div>
            )}
            {seen.size > 0 && (
              <button type="button" className="ns-hint-btn" onClick={reset}>
                reset
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* A decorative "unlock everything" gate — enter the code to open every lesson,
   key, and game at once (so a returning player isn't re-gated behind the path).
   The code ships in the bundle; it's a friendly bypass, not real security. */
const UnlockGate = ({ unlocked, onUnlockAll }) => {
  const [code, setCode] = useState("");
  const [wrong, setWrong] = useState(false);
  const submit = () => {
    if (code.trim() === UNLOCK_CODE) {
      onUnlockAll();
      setWrong(false);
    } else {
      setWrong(true);
    }
  };
  return (
    <div className="ns-unlock">
      <div className="ns-set-h">unlock everything</div>
      {unlocked ? (
        <p className="ns-set-sub ns-unlock-on">
          ✓ Everything's unlocked — every lesson, key, and game is open.
        </p>
      ) : (
        <>
          <p className="ns-set-sub">
            Already made progress before? Enter your unlock code to open every
            lesson, key, and game right away.
          </p>
          <div className="ns-unlock-row">
            <input
              className="ns-unlock-input"
              value={code}
              aria-label="unlock code"
              placeholder="unlock code"
              onChange={(e) => {
                setCode(e.target.value);
                setWrong(false);
              }}
              onKeyDown={(e) => e.key === "Enter" && code.trim() && submit()}
            />
            <button className="btn" onClick={submit} disabled={!code.trim()}>
              unlock
            </button>
          </div>
          {wrong && (
            <p className="ns-set-sub ns-unlock-err">That code isn't right.</p>
          )}
        </>
      )}
    </div>
  );
};

const SettingsScreen = ({ settings, onChange, onBack, unlocked, onUnlockAll }) => {
  const bpm = beatMsToBpm(settings.tempo);
  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onBack} aria-label="back">
            <Icon name="back" />
          </button>
          <div className="subbar-title">settings</div>
        </div>
        <div className="scroll">
          {CAN_PLAY ? (
            <>
              <p className="games-intro">
                How playback feels across lessons and games. These save with your
                progress.
              </p>

              <div className="ns-set-row">
                <div className="ns-set-label">
                  <div className="ns-set-h">sound</div>
                  <div className="ns-set-sub">
                    {settings.muted ? "muted — no audio anywhere" : "audio on"}
                  </div>
                </div>
                <button
                  role="switch"
                  aria-checked={!settings.muted}
                  aria-label="sound"
                  className={`ns-switch ${settings.muted ? "" : "on"}`}
                  onClick={() => onChange({ muted: !settings.muted })}
                >
                  <span className="knob" />
                </button>
              </div>

              <div className={`ns-set-row ${settings.muted ? "disabled" : ""}`}>
                <div className="ns-set-label">
                  <div className="ns-set-h">ear-training tempo</div>
                  <div className="ns-set-sub">{bpm} bpm</div>
                </div>
                <input
                  type="range"
                  className="ns-range"
                  aria-label="ear-training tempo (bpm)"
                  min={beatMsToBpm(TEMPO_MAX)}
                  max={beatMsToBpm(TEMPO_MIN)}
                  step={1}
                  value={bpm}
                  disabled={settings.muted}
                  onChange={(e) => onChange({ tempo: bpmToBeatMs(Number(e.target.value)) })}
                />
              </div>

              <div className={`ns-set-row ${settings.muted ? "disabled" : ""}`}>
                <div className="ns-set-label">
                  <div className="ns-set-h">count-in</div>
                  <div className="ns-set-sub">
                    a one-bar click before each loop
                  </div>
                </div>
                <button
                  role="switch"
                  aria-checked={settings.countIn}
                  aria-label="count-in"
                  className={`ns-switch ${settings.countIn ? "on" : ""}`}
                  disabled={settings.muted}
                  onClick={() => onChange({ countIn: !settings.countIn })}
                >
                  <span className="knob" />
                </button>
              </div>

              <div className="ns-set-preview">
                {/* Preview honours the live settings via the context provider. */}
                <AudioSettingsContext.Provider
                  value={{ tempo: settings.tempo, countIn: settings.countIn }}
                >
                  <PlayButton chords={SAMPLE} big label="test the sound" />
                </AudioSettingsContext.Provider>
                {settings.muted && (
                  <div className="ns-set-sub">unmute to hear it</div>
                )}
              </div>
            </>
          ) : (
            <p className="games-intro">
              This browser can't synthesize audio, so the playback options are
              hidden.
            </p>
          )}

          {MIDI_SUPPORTED ? (
            <MidiSetup settings={settings} onChange={onChange} />
          ) : (
            <div className="ns-midi-setup">
              <div className="ns-set-h">MIDI keyboard</div>
              <p className="ns-set-sub">
                This browser doesn't support Web MIDI, so a hardware keyboard
                can't connect here. Desktop Chrome or Edge works; Safari and most
                phones don't.
              </p>
            </div>
          )}

          <UnlockGate unlocked={unlocked} onUnlockAll={onUnlockAll} />

          <div className="footnote">
            <Keys />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsScreen;
